// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { GENERATOR_CODES, SNF_PHASES, isNeutralizationTheoremCode, witnessKindForTheorem, } from "@csk/core";
import { WASM_THEOREM_CODES, WASM_THEOREM_META } from "./meta.js";
import { admitSeamBridgeAttempt, parseSeamNormalFormTraceJson, parseWasmSeamDescriptor, } from "./seam-bridge-kernel.js";
import { parseWasmSquareDecision, squareDecisionFor } from "./squares.js";
const VALID_TRACE_JSON = JSON.stringify(SNF_PHASES);
const FEEDBACK_TRACE_JSON = JSON.stringify(["B1", "B2", "B4", "B2"]);
function theoremCode(value) {
    if (!isNeutralizationTheoremCode(value)) {
        throw new Error(`Unknown theorem code in WASM metadata: ${value}`);
    }
    return value;
}
function theoremCodes() {
    return WASM_THEOREM_CODES.map((code) => theoremCode(code));
}
function otherTheorem(code) {
    const other = theoremCodes().find((candidate) => candidate !== code);
    if (other === undefined) {
        throw new Error("At least two theorem codes are required");
    }
    return other;
}
function otherGenerator(generator) {
    const other = GENERATOR_CODES.find((candidate) => candidate !== generator);
    if (other === undefined) {
        throw new Error("At least two generator codes are required");
    }
    return other;
}
function kernelStatus(kernel, theoremCode, witnessTheoremCode, traceJson, forbiddenSquare) {
    return kernel.bridge_admit_status(theoremCode, witnessTheoremCode ?? "", traceJson, forbiddenSquare);
}
function coreStatus(theoremCode, witnessTheoremCode, traceJson, forbiddenSquare) {
    let trace;
    try {
        trace = parseSeamNormalFormTraceJson(traceJson);
    }
    catch {
        return "rejected-invalid-lifecycle";
    }
    return admitSeamBridgeAttempt({
        theoremCode,
        witnessTheoremCode,
        trace,
        forbiddenSquare,
    }).status;
}
function statusMatchesCore(kernel, theoremCode, witnessTheoremCode, traceJson, forbiddenSquare) {
    return (kernelStatus(kernel, theoremCode, witnessTheoremCode, traceJson, forbiddenSquare) ===
        coreStatus(theoremCode, witnessTheoremCode, traceJson, forbiddenSquare));
}
function squareDecisionMatchesCore(kernel, theoremCode, generatorCode) {
    try {
        const actual = parseWasmSquareDecision(kernel.square_decide(theoremCode, generatorCode));
        const expected = squareDecisionFor(theoremCode, generatorCode);
        return (actual.status === expected.status &&
            actual.squareId === expected.squareId &&
            kernel.square_is_forbidden(theoremCode, generatorCode) ===
                (expected.status === "forbidden"));
    }
    catch {
        return false;
    }
}
function statefulBridgeStatus(serialized) {
    try {
        const value = JSON.parse(serialized);
        if (typeof value !== "object" || value === null)
            return null;
        const status = value["status"];
        return typeof status === "string" ? status : null;
    }
    catch {
        return null;
    }
}
function refusesReplay(kernel, theoremCode) {
    const seam = kernel.seam_define(theoremCode, `stateful:${theoremCode}`);
    const witness = kernel.bridge_witness_mint(theoremCode, `wasm-seam:${theoremCode}`, 1);
    const bridge = kernel.bridge_propose(seam, witness, "typescript-host", "wasm-kernel");
    const first = statefulBridgeStatus(kernel.bridge_admit(bridge, `wasm-seam:${theoremCode}`, VALID_TRACE_JSON, false));
    const receipt = kernel.bridge_receipt(bridge);
    const second = statefulBridgeStatus(kernel.bridge_admit(bridge, `wasm-seam:${theoremCode}`, VALID_TRACE_JSON, false));
    return (first === "admitted" &&
        receipt.includes(`"theoremCode":"${theoremCode}"`) &&
        kernel.bridge_lifecycle(bridge) === "admitted" &&
        kernel.bridge_witness_remaining_uses(witness) === 0 &&
        second === "rejected-invalid-lifecycle");
}
function probeDetail(probe) {
    const failures = [
        ["theorem metadata", probe.theoremAdvertised],
        ["seam descriptor", probe.descriptorMatches],
        ["SNF trace", probe.snfTraceAccepted],
        ["governed bridge", probe.governedBridgeAdmitted],
        ["square decision", probe.squareDecisionMatches],
        ["square-backed bridge", probe.squareBackedAdmissionRefused],
        ["missing witness", probe.missingWitnessRefused],
        ["forbidden square", probe.forbiddenSquareRefused],
        ["wrong witness", probe.wrongWitnessRefused],
        ["feedback trace", probe.feedbackTraceRefused],
        ["witness replay", probe.replayRefused],
    ]
        .filter(([, passed]) => passed !== true)
        .map(([name]) => name);
    return failures.length === 0
        ? "seam descriptor, SNF trace, bridge admission, and adversarial refusals agree"
        : `failed checks: ${failures.join(", ")}`;
}
function validateSeamBridgeProbe(kernel, theoremCode) {
    const meta = WASM_THEOREM_META[theoremCode];
    if (meta === undefined) {
        throw new Error(`Missing theorem metadata for ${theoremCode}`);
    }
    const descriptor = parseWasmSeamDescriptor(kernel.seam_descriptor(theoremCode));
    const wrongWitness = otherTheorem(theoremCode);
    const admittedGenerator = otherGenerator(meta.generator);
    const theoremAdvertised = kernel.csk_available_theorems().includes(meta.name);
    const descriptorMatches = descriptor !== null &&
        descriptor.theoremCode === theoremCode &&
        descriptor.theoremName === meta.name &&
        descriptor.seamOrientation === meta.seamType &&
        descriptor.forbiddenGenerator === meta.generator &&
        descriptor.requiredWitnessKind === witnessKindForTheorem(theoremCode);
    const snfTraceAccepted = kernel.seam_validate_snf_trace(VALID_TRACE_JSON) &&
        !kernel.seam_validate_snf_trace(FEEDBACK_TRACE_JSON);
    const governedBridgeAdmitted = kernelStatus(kernel, theoremCode, theoremCode, VALID_TRACE_JSON, false) ===
        "admitted" &&
        statusMatchesCore(kernel, theoremCode, theoremCode, VALID_TRACE_JSON, false);
    const squareDecisionMatches = squareDecisionMatchesCore(kernel, theoremCode, meta.generator) &&
        squareDecisionMatchesCore(kernel, theoremCode, admittedGenerator);
    const squareBackedAdmissionRefused = kernel.bridge_admit_square_status(theoremCode, theoremCode, VALID_TRACE_JSON, meta.generator) === "rejected-forbidden-square" &&
        kernel.bridge_admit_square_status(theoremCode, theoremCode, VALID_TRACE_JSON, admittedGenerator) === "admitted";
    const missingWitnessRefused = kernelStatus(kernel, theoremCode, null, VALID_TRACE_JSON, false) ===
        "rejected-missing-witness" &&
        statusMatchesCore(kernel, theoremCode, null, VALID_TRACE_JSON, false);
    const forbiddenSquareRefused = kernelStatus(kernel, theoremCode, theoremCode, VALID_TRACE_JSON, true) ===
        "rejected-forbidden-square" &&
        statusMatchesCore(kernel, theoremCode, theoremCode, VALID_TRACE_JSON, true);
    const wrongWitnessRefused = kernelStatus(kernel, theoremCode, wrongWitness, VALID_TRACE_JSON, false) ===
        "rejected-invalid-lifecycle" &&
        statusMatchesCore(kernel, theoremCode, wrongWitness, VALID_TRACE_JSON, false);
    const feedbackTraceRefused = kernelStatus(kernel, theoremCode, theoremCode, FEEDBACK_TRACE_JSON, false) === "rejected-invalid-lifecycle" &&
        statusMatchesCore(kernel, theoremCode, theoremCode, FEEDBACK_TRACE_JSON, false);
    const partial = {
        theoremCode,
        theoremName: meta.name,
        seamOrientation: meta.seamType,
        forbiddenGenerator: meta.generator,
        requiredWitnessKind: witnessKindForTheorem(theoremCode),
        theoremAdvertised,
        descriptorMatches,
        snfTraceAccepted,
        governedBridgeAdmitted,
        squareDecisionMatches,
        squareBackedAdmissionRefused,
        missingWitnessRefused,
        forbiddenSquareRefused,
        wrongWitnessRefused,
        feedbackTraceRefused,
        replayRefused: refusesReplay(kernel, theoremCode),
    };
    return { ...partial, detail: probeDetail(partial) };
}
function refusedGeneratorSet(probes) {
    const refused = new Set(probes
        .filter((probe) => probe.forbiddenSquareRefused)
        .map((probe) => probe.forbiddenGenerator));
    return GENERATOR_CODES.filter((generator) => refused.has(generator));
}
export function validateWasmSeamBridgeContract(kernel, backend) {
    const probes = theoremCodes().map((code) => validateSeamBridgeProbe(kernel, code));
    const forbiddenGeneratorsRefused = refusedGeneratorSet(probes);
    const passed = probes.every((probe) => probe.theoremAdvertised &&
        probe.descriptorMatches &&
        probe.snfTraceAccepted &&
        probe.governedBridgeAdmitted &&
        probe.squareDecisionMatches &&
        probe.squareBackedAdmissionRefused &&
        probe.missingWitnessRefused &&
        probe.forbiddenSquareRefused &&
        probe.wrongWitnessRefused &&
        probe.feedbackTraceRefused &&
        probe.replayRefused) && forbiddenGeneratorsRefused.length === GENERATOR_CODES.length;
    return {
        backend,
        passed,
        seamCount: probes.length,
        forbiddenGeneratorsRefused,
        probes,
    };
}
export function assertWasmSeamBridgeContract(kernel, backend) {
    const receipt = validateWasmSeamBridgeContract(kernel, backend);
    if (receipt.passed) {
        return receipt;
    }
    const failures = receipt.probes
        .filter((probe) => probe.detail.startsWith("failed checks:"))
        .map((probe) => `${probe.theoremCode} ${probe.theoremName}: ${probe.detail}`)
        .join("; ");
    throw new Error(`Wasm seam/bridge validation failed for ${backend}: ${failures}`);
}
//# sourceMappingURL=seam-bridge.js.map